import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  MessageCircle,
  Wifi,
  Building2,
  Home,
  Shield,
  Zap,
  Network,
  Headphones,
  Cable,
  Gauge,
  CheckCircle2,
} from "lucide-react";
import Header from "../components/nexus/Header";
import Footer from "../components/nexus/Footer";
import { SITE } from "../lib/site";

const PLANS = [
  {
    audience: "Residencial",
    icon: Home,
    accent: "from-cyan-500/20 to-transparent",
    highlight: "Fibra 100% óptica",
    features: [
      "Planos sob medida para o seu consumo",
      "Roteador Wi-Fi incluso",
      "Possibilidade de upgrade para rede mesh",
      "Suporte humano e próximo",
    ],
  },
  {
    audience: "Empresarial",
    icon: Building2,
    accent: "from-fuchsia-500/20 to-transparent",
    highlight: "Internet que não para",
    features: [
      "Planos Business com SLA diferenciado",
      "Dual-balance e failover para alta disponibilidade",
      "Link dedicado ponto a ponto",
      "Atendimento priorizado com equipe técnica",
    ],
  },
  {
    audience: "Condomínios",
    icon: Network,
    accent: "from-amber-500/20 to-transparent",
    highlight: "Projetos chave-na-mão",
    features: [
      "Infraestrutura ótica dedicada",
      "Gestão centralizada do condomínio",
      "Cobertura completa em áreas comuns",
      "Plano especial para moradores",
    ],
  },
];

const DIFF = [
  {
    icon: Zap,
    title: "Planos que cabem no bolso",
    desc: "Mensalidade competitiva sem abrir mão da qualidade — velocidade estável e atendimento humanizado.",
  },
  {
    icon: Shield,
    title: "Dual-balance & Failover",
    desc: "Sua empresa nunca fica sem internet: redundância automática entre links para zero downtime.",
  },
  {
    icon: Wifi,
    title: "Rede Mesh sob medida",
    desc: "Wi-Fi cobrindo toda a residência ou empresa, sem quedas entre ambientes e sem áreas mortas.",
  },
  {
    icon: Cable,
    title: "Link Dedicado corporativo",
    desc: "Banda garantida, IP fixo e priorização total para operações críticas e aplicações em nuvem.",
  },
  {
    icon: Headphones,
    title: "Atendimento priorizado",
    desc: "Suporte técnico regional com resposta rápida, visitas agendadas e acompanhamento contínuo.",
  },
  {
    icon: Gauge,
    title: "Otimização de performance",
    desc: "Configuração profissional da sua rede para extrair o máximo do seu plano em todos os dispositivos.",
  },
];

const WPP_INTERNET =
  "https://wa.me/5516981845736?text=" +
  encodeURIComponent(
    "Olá, Nexus Internet! Gostaria de conhecer os planos disponíveis na minha região."
  );

export const InternetPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    document.title = "Nexus Internet — Grupo Nexus";
  }, []);

  return (
    <>
      <Header />
      <main className="bg-[#050505] text-white">
        {/* HERO */}
        <section className="relative pt-32 md:pt-40 pb-24 md:pb-28 overflow-hidden">
          <div className="nx-blob bg-[#4c2e9b] w-[540px] h-[540px] -top-40 -left-24" aria-hidden />
          <div className="nx-blob bg-[#00f0ff]/30 w-[380px] h-[380px] bottom-0 right-0" aria-hidden />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(76,46,155,0.25),transparent_55%)]" aria-hidden />

          <div className="relative max-w-7xl mx-auto px-6 md:px-8">
            <Link
              to="/"
              data-testid="back-home"
              className="inline-flex items-center gap-2 text-[13px] text-white/60 hover:text-white mb-10"
            >
              <ArrowLeft size={14} /> Voltar para o Grupo Nexus
            </Link>

            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 backdrop-blur-md">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="overline text-white/80">Nexus Internet · Araraquara & Américo Brasiliense</span>
                </div>
                <h1 className="font-display mt-7 text-[38px] leading-[1.03] sm:text-5xl md:text-6xl lg:text-[68px] font-light tracking-tight">
                  Internet <span className="nx-rainbow-text font-medium">estável</span>, plano
                  que <span className="text-white/85 font-medium">cabe no bolso</span> e suporte
                  que <span className="italic font-light">resolve</span>.
                </h1>
                <p className="mt-7 max-w-2xl text-base md:text-lg text-[#b3b0c2] leading-relaxed">
                  Soluções completas de conectividade para{" "}
                  <strong className="text-white font-medium">residências</strong>,{" "}
                  <strong className="text-white font-medium">empresas</strong> e{" "}
                  <strong className="text-white font-medium">condomínios</strong> em Araraquara e
                  Américo Brasiliense. Do plano residencial ao link dedicado corporativo — tudo
                  para você tirar o máximo da sua internet.
                </p>

                <div className="mt-9 flex flex-col sm:flex-row gap-3">
                  <a
                    href={WPP_INTERNET}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="internet-hero-whatsapp"
                    className="nx-btn-primary"
                  >
                    <MessageCircle size={16} /> Falar no WhatsApp
                  </a>
                  <a href="#planos" className="nx-btn-secondary">
                    Ver planos disponíveis
                  </a>
                </div>

                <ul className="mt-10 grid sm:grid-cols-2 gap-3 max-w-xl">
                  {[
                    "Atendimento priorizado",
                    "Mensalidade acessível",
                    "Dual-balance e failover",
                    "Rede mesh sob medida",
                  ].map((i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/85">
                      <CheckCircle2 size={16} className="text-emerald-300 mt-0.5" />
                      {i}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-[28px] overflow-hidden border border-white/10 shadow-[0_40px_120px_-20px_rgba(76,46,155,0.4)]">
                  <img
                    src="https://static.prod-images.emergentagent.com/jobs/45876ead-02aa-42fd-bdbd-b2c7f606006c/images/0e96aec0917c41999454dbfcdeaf9321f6dcc3d47283710aeb5f21a658a8abe9.png"
                    alt="Fibra óptica Nexus Internet"
                    className="w-full h-[460px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
                    <div className="rounded-xl border border-white/15 bg-black/60 backdrop-blur px-4 py-3">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                        Cobertura
                      </div>
                      <div className="font-display text-lg">Araraquara · Américo Brasiliense</div>
                    </div>
                    <div className="rounded-xl border border-white/15 bg-black/60 backdrop-blur px-4 py-3 text-right">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                        Fibra óptica
                      </div>
                      <div className="font-display text-lg nx-rainbow-text">100%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PLANOS */}
        <section id="planos" className="relative py-20 md:py-28 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="max-w-2xl">
              <span className="overline">Para quem é</span>
              <h2 className="font-display mt-4 text-3xl md:text-5xl font-medium tracking-tight leading-[1.08]">
                Planos para casa, empresa e{" "}
                <span className="text-white/60 italic font-light">condomínio</span>.
              </h2>
              <p className="mt-5 text-[15px] text-[#b3b0c2] max-w-xl">
                Três frentes, um padrão de qualidade. Dimensionamos o projeto conforme o uso e o
                tamanho da sua operação.
              </p>
            </div>

            <div className="mt-12 grid md:grid-cols-3 gap-5">
              {PLANS.map((p) => {
                const Icon = p.icon;
                return (
                  <article
                    key={p.audience}
                    data-testid={`internet-plan-${p.audience}`}
                    className="relative overflow-hidden rounded-[22px] border border-white/10 bg-[#0A0810] p-8 group transition-all hover:-translate-y-1 hover:border-white/25 hover:shadow-[0_8px_32px_rgba(76,46,155,0.25)]"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-b ${p.accent} opacity-50`} />
                    <div className="relative">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                          <Icon size={18} strokeWidth={1.5} />
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                          {p.highlight}
                        </span>
                      </div>
                      <h3 className="font-display mt-7 text-2xl md:text-[26px] font-medium">
                        {p.audience}
                      </h3>
                      <ul className="mt-6 space-y-3">
                        {p.features.map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-[14px] text-white/85">
                            <CheckCircle2 size={15} className="text-emerald-300 mt-0.5 shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <a
                        href={WPP_INTERNET}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`plan-${p.audience}-whatsapp`}
                        className="mt-8 nx-btn-secondary w-full justify-center"
                      >
                        <MessageCircle size={14} /> Consultar disponibilidade
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* DIFERENCIAIS */}
        <section className="relative py-20 md:py-28 border-t border-white/5 bg-gradient-to-b from-transparent to-[#07060c]">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="max-w-2xl">
              <span className="overline">Tecnologia a favor do cliente</span>
              <h2 className="font-display mt-4 text-3xl md:text-5xl font-medium tracking-tight leading-[1.08]">
                Muito mais que{" "}
                <span className="text-white/60 italic font-light">velocidade</span>.
              </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border border-white/5 rounded-[24px] overflow-hidden bg-[#08070d]">
              {DIFF.map((d, i) => {
                const Icon = d.icon;
                return (
                  <div
                    key={d.title}
                    data-testid={`internet-diff-${i}`}
                    className="relative p-8 md:p-10 border-b border-r border-white/5 group hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-[#1a103c] to-[#0A0810]">
                        <Icon size={18} strokeWidth={1.5} />
                      </span>
                      <span className="font-display text-sm text-white/45">0{i + 1}</span>
                    </div>
                    <h3 className="font-display mt-6 text-xl font-medium leading-snug">{d.title}</h3>
                    <p className="mt-3 text-[14.5px] text-[#9f9caf] leading-relaxed">{d.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="nx-rainbow-border relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#12093a] via-[#0A0810] to-black p-10 md:p-16 text-center" data-active="true">
              <div className="nx-blob bg-[#4c2e9b] w-[500px] h-[500px] -top-40 -left-32" aria-hidden />
              <div className="nx-blob bg-[#00f0ff]/30 w-[360px] h-[360px] -bottom-24 -right-10" aria-hidden />
              <div className="relative max-w-3xl mx-auto">
                <span className="overline">Pronto para migrar?</span>
                <h2 className="font-display mt-5 text-3xl md:text-5xl font-medium tracking-tight leading-[1.05]">
                  Fale agora com a equipe{" "}
                  <span className="nx-rainbow-text">Nexus Internet</span>.
                </h2>
                <p className="mt-5 text-[15px] md:text-base text-[#b3b0c2]">
                  Atendimento rápido no WhatsApp. Consultamos disponibilidade na sua rua e
                  montamos o plano ideal para você, sua empresa ou seu condomínio.
                </p>
                <div className="mt-9 flex flex-wrap justify-center gap-3">
                  <a
                    href={WPP_INTERNET}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="internet-cta-whatsapp"
                    className="nx-btn-primary"
                  >
                    <MessageCircle size={16} /> WhatsApp {SITE.phoneLabel}
                  </a>
                  <Link to="/#contato" className="nx-btn-secondary">
                    Enviar mensagem
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default InternetPage;
